// useFeedbackWidget — self-serve "report a problem" widget, scoped to the
// contexts where it's actually useful (#3316: a prospective operator stuck
// on signup had no way to tell us except phoning in).
//
// Eligible: marketing apex (isMarketingHost(), where #3316 happened) OR one
// of Stemne's own operator/pre-account chrome prefixes on a tenant host.
// Re-evaluated on every navigation (not just once at boot) because a single
// SPA session can cross between eligible and ineligible without a full
// reload — an operator can go from a tenant's public booking preview to
// /auth/login in one client-side nav.
//
// STEMNE_CHROME_PREFIXES is a hand-maintained allowlist, not a route-meta-
// derived one — this app has no existing "is this route tenant-branded"
// flag to key off, and adding one across ~20 route definitions is a bigger
// change than this widget warrants. Deliberately NOT the whole operator
// dashboard (a persistent floating button there fights the "one primary
// action per surface" design rule, and operators already have Hjelp in the
// sidebar as a support path) — scoped to pre-account chrome plus the three
// dashboard pages an operator actually goes to for help or account issues:
//   - /auth/* (login, passkey, recovery) renders Stemne's own branding,
//     confirmed by screenshot — no tenant theming applied there.
//   - /setup/* is the onboarding wizard, /dpa-accept + /dpa-required the
//     DPA gate — both Stemne chrome, pre- or just-post-account.
//   - /admin/invite/:token + /admin/confirm-email are pre-account utility
//     landing pages (invite acceptance, email-change confirmation) — no
//     dashboard chrome around them.
//   - /dashboard/help, /dashboard/settings, /dashboard/account — the
//     dashboard surfaces most likely to be where an operator wants to
//     reach us, without putting the button on every operator screen.
// Everything else on a tenant host (/, /portal, /booking*, /rom, /meeting,
// /join, …) is the OPERATOR's branded surface (useTenant fetches their
// colors/fonts/name) or the end customer's own flow — a Stemne-colored
// "give us feedback" button there would be off-brand and wrong-audience:
// the end customer booking a meeting isn't who should be prompted to send
// *us* feedback.
//
// autoInject must be false on the feedbackIntegration() config in main.ts —
// this composable is the only thing that ever calls createWidget()/
// removeFromDom(), so a second auto-injected instance would double up.
import * as Sentry from '@sentry/vue'
import { watch } from 'vue'
import type { Router } from 'vue-router'
import { isMarketingHost } from '@/utils/platformHost'
import { sentryReplayOptions } from '@/utils/sentryPrivacy'
import { createFeedbackReplayCapture } from '@/utils/sentryReplayFeedback'

const STEMNE_CHROME_PREFIXES = [
  '/auth',
  '/admin',
  '/setup',
  '/dpa-accept',
  '/dpa-required',
  '/dashboard/help',
  '/dashboard/settings',
  '/dashboard/account',
]

function isEligiblePath(path: string): boolean {
  return isMarketingHost() || STEMNE_CHROME_PREFIXES.some((prefix) => path.startsWith(prefix))
}

export function useFeedbackWidget(router: Router): void {
  const feedback = Sentry.getFeedback()
  if (!feedback) return // No DSN configured (dev/CI) — Sentry never initialized.

  const replayCapture = createFeedbackReplayCapture(
    sentryReplayOptions(import.meta.env).feedbackEnabled,
  )
  let actor: ReturnType<typeof feedback.createWidget> | null = null

  watch(
    () => router.currentRoute.value.path,
    (path) => {
      const eligible = isEligiblePath(path)
      if (eligible && !actor) {
        actor = feedback.createWidget({
          onFormOpen: replayCapture.begin,
          onSubmitSuccess: replayCapture.submit,
          onSubmitError: replayCapture.discard,
          onFormClose: replayCapture.discard,
        })
      } else if (!eligible && actor) {
        actor.removeFromDom()
        replayCapture.discard()
        actor = null
      }
    },
    { immediate: true },
  )
}
